import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, AlertTriangle, CheckCircle2, Flame, ListTodo, FileText, Pin, ChevronRight, Calendar, Target, Trophy, Users, MapPin, Activity } from 'lucide-react';
import ActivityFeed from './ActivityFeed';

export default function DailyBriefing({ api, onJump }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [googleEvents, setGoogleEvents] = useState([]);

  const fetchBriefing = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: briefing }, googleResp] = await Promise.all([
        api.get('/ceo-life/briefing'),
        api.get('/calendar/upcoming?days=7').catch(() => ({ data: { events: [] } })),
      ]);
      setData(briefing);
      setGoogleEvents(googleResp.data?.events || []);
    } catch {
      setData(null);
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchBriefing(); }, [fetchBriefing]);

  if (loading) return <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" style={{ color: 'var(--accent)' }} /></div>;
  if (!data) return <div className="ceo-empty">Could not load today's briefing.</div>;

  const { stats, todos_today, todos_overdue, top_priority, pinned_notes,
          upcoming_meetings = [], at_risk_goals = [], active_projects = [],
          project_tasks_today = [], project_tasks_overdue = [] } = data;

  const toggleProjectTask = async (row) => {
    try {
      await api.post(`/ceo-life/projects/${row.project_id}/tasks/${row.task_id}/toggle`);
      fetchBriefing();
    } catch { /* ignore */ }
  };

  return (
    <div className="space-y-6" data-testid="ceo-briefing">
      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Pending" value={stats.pending_total} accent="var(--accent)" />
        <StatCard label="Due today" value={todos_today.length} accent="var(--gold)" />
        <StatCard label="Overdue" value={stats.overdue_count} accent={stats.overdue_count > 0 ? 'var(--critical)' : 'var(--text-low)'} />
        <StatCard label="Done today" value={stats.completed_today} accent="var(--good)" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Upcoming meetings + Google Calendar events */}
        <Section
          icon={Calendar}
          iconColor="var(--accent)"
          title="Next meetings"
          count={upcoming_meetings.length + googleEvents.length}
          empty="No upcoming meetings."
          onSeeAll={() => onJump('meetings')}
        >
          {upcoming_meetings.map(m => (
            <div key={m.id} className="ceo-todo-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
              <div className="ceo-todo-title" style={{ fontWeight: 600 }}>{m.title}</div>
              <div className="text-[11px] flex items-center gap-3 flex-wrap" style={{ color: 'var(--text-low)' }}>
                <span>{formatDate(m.date)}{m.start_time?` · ${m.start_time}`:''}</span>
                {m.location && <span className="flex items-center gap-1"><MapPin className="h-2.5 w-2.5" />{m.location}</span>}
                {(m.attendees||[]).length > 0 && <span className="flex items-center gap-1"><Users className="h-2.5 w-2.5" />{m.attendees.length}</span>}
              </div>
            </div>
          ))}
          {googleEvents.map(ev => (
            <a
              key={`g-${ev.id}`}
              href={ev.html_link || '#'}
              target="_blank"
              rel="noreferrer"
              className="ceo-todo-row"
              style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4, textDecoration: 'none' }}
              data-testid={`ceo-google-event-${ev.id}`}
            >
              <div className="ceo-todo-title flex items-center gap-2" style={{ fontWeight: 600 }}>
                <span className="ceo-pill" style={{ padding: '1px 6px', fontSize: 9, color: '#4CD964' }}>Google</span>
                {ev.title}
              </div>
              <div className="text-[11px] flex items-center gap-3 flex-wrap" style={{ color: 'var(--text-low)' }}>
                <span>{formatGoogleStart(ev.start, ev.all_day)}</span>
                {ev.location && <span className="flex items-center gap-1"><MapPin className="h-2.5 w-2.5" />{ev.location}</span>}
              </div>
            </a>
          ))}
        </Section>

        {/* Active projects */}
        <Section
          icon={Target}
          iconColor="var(--good)"
          title="Active projects"
          count={active_projects.length}
          empty="No active projects."
          onSeeAll={() => onJump('projects')}
        >
          {active_projects.map(p => (
            <div key={p.id} className="ceo-todo-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
              <div className="flex items-center gap-2">
                <div className="ceo-todo-title flex-1">{p.name}</div>
                <span className="text-xs font-bold" style={{ color: 'var(--text)' }}>{p.percent_done || 0}%</span>
              </div>
              <div style={{ height: 4, borderRadius: 2, background: 'var(--ink)', overflow: 'hidden' }}>
                <div style={{ width: `${p.percent_done || 0}%`, height: '100%', background: p.color || 'var(--accent)' }} />
              </div>
            </div>
          ))}
        </Section>
      </div>

      {/* Project tasks — due today + overdue, with inline complete */}
      {(project_tasks_today.length + project_tasks_overdue.length) > 0 && (
        <Section
          icon={Target}
          iconColor={project_tasks_overdue.length > 0 ? 'var(--critical)' : 'var(--gold)'}
          title="Project tasks"
          count={project_tasks_today.length + project_tasks_overdue.length}
          onSeeAll={() => onJump('projects')}
        >
          {[...project_tasks_overdue, ...project_tasks_today].map(t => (
            <button
              key={t.task_id}
              onClick={() => toggleProjectTask(t)}
              className="ceo-todo-row w-full text-left"
              style={{ alignItems: 'flex-start' }}
              data-testid={`ceo-briefing-ptask-${t.task_id}`}
            >
              <input type="checkbox" className="ceo-checkbox mt-1" onChange={() => toggleProjectTask(t)} onClick={e => e.stopPropagation()} />
              <div className="flex-1 min-w-0">
                <div className="ceo-todo-title">{t.title}</div>
                <div className="text-[11px] mt-0.5 flex items-center gap-2 flex-wrap" style={{ color: t.due_date < (data.date) ? 'var(--critical)' : 'var(--text-low)' }}>
                  <span className="ceo-priority-dot" style={{ background: t.project_color || 'var(--accent)', boxShadow: 'none' }} />
                  <span style={{ color: 'var(--text-dim)' }}>{t.project_name}</span>
                  <span>· {t.due_date < (data.date) ? 'Overdue ' : ''}{formatDue(t.due_date)}</span>
                  {t.owner && <span>· {t.owner}</span>}
                </div>
              </div>
            </button>
          ))}
        </Section>
      )}

      {/* At-risk goals — alert tone */}
      {at_risk_goals.length > 0 && (
        <Section
          icon={Trophy}
          iconColor="var(--warn)"
          title="Goals needing attention"
          count={at_risk_goals.length}
          onSeeAll={() => onJump('goals')}
        >
          {at_risk_goals.map(g => (
            <div key={g.id} className="ceo-todo-row">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" style={{ color: g.status === 'off_track' ? 'var(--danger)' : 'var(--warn)', marginTop: 2 }} />
              <div className="flex-1 min-w-0">
                <div className="ceo-todo-title">{g.title}</div>
                <div className="text-[11px] mt-0.5" style={{ color: 'var(--text-low)' }}>{g.quarter} · {g.status.replace('_', ' ')}</div>
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* Overdue — alert tone */}
      {todos_overdue.length > 0 && (
        <Section
          icon={AlertTriangle}
          iconColor="var(--critical)"
          title="Overdue tasks"
          count={todos_overdue.length}
          onSeeAll={() => onJump('todos')}
        >
          {todos_overdue.slice(0, 5).map(t => (
            <BriefRow key={t.id} title={t.title} due={t.due_date} priority={t.priority} overdue />
          ))}
        </Section>
      )}

      {/* Today's todos */}
      <Section
        icon={ListTodo}
        iconColor="var(--gold)"
        title="Today"
        count={todos_today.length}
        empty="Nothing scheduled for today. Quiet day, or time to plan?"
        onSeeAll={() => onJump('todos')}
      >
        {todos_today.map(t => (
          <BriefRow key={t.id} title={t.title} due={t.due_date} priority={t.priority} />
        ))}
      </Section>

      {/* Top priority */}
      <Section
        icon={Flame}
        iconColor="var(--warn)"
        title="Top priority"
        count={top_priority.length}
        empty="No high-priority items right now."
        onSeeAll={() => onJump('todos')}
      >
        {top_priority.map(t => (
          <BriefRow key={t.id} title={t.title} due={t.due_date} priority={t.priority} />
        ))}
      </Section>

      {/* Pinned notes */}
      {pinned_notes.length > 0 && (
        <Section
          icon={Pin}
          iconColor="var(--accent)"
          title="Pinned notes"
          count={pinned_notes.length}
          onSeeAll={() => onJump('notes')}
        >
          {pinned_notes.map(n => (
            <div key={n.id} className="ceo-todo-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
              <div className="ceo-todo-title" style={{ fontWeight: 600 }}>{n.title || 'Untitled note'}</div>
              {n.body && <p className="text-xs line-clamp-2" style={{ color: 'var(--text-dim)' }}>{n.body}</p>}
            </div>
          ))}
        </Section>
      )}

      {/* Recent activity — last 8 events */}
      <Section icon={Activity} iconColor="var(--accent)" title="Recent activity" onSeeAll={() => onJump('activity')}>
        <ActivityFeed api={api} compact limit={8} />
      </Section>
    </div>
  );
}

function formatDate(d) {
  if (!d) return '';
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); }
  catch { return d; }
}

function formatGoogleStart(iso, allDay) {
  if (!iso) return '';
  try {
    if (allDay) {
      // Google all-day events are returned as YYYY-MM-DD
      return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) + ' · all day';
    }
    const dt = new Date(iso);
    return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) + ' · ' +
      dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
}

function StatCard({ label, value, accent }) {
  return (
    <div className="ceo-card p-4" data-testid={`ceo-stat-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="ceo-eyebrow" style={{ color: accent }}>{label}</div>
      <div className="ceo-stat-num mt-1">{value}</div>
    </div>
  );
}

function Section({ icon: Icon, iconColor, title, count, empty, onSeeAll, children }) {
  const items = React.Children.toArray(children);
  return (
    <div className="ceo-card-elevated p-5" data-testid={`ceo-section-${title.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4" style={{ color: iconColor }} />
          <h3 className="text-sm font-bold" style={{ color: 'var(--text)' }}>{title}</h3>
          {typeof count === 'number' && (
            <span className="ceo-pill" style={{ padding: '2px 8px', fontSize: 10 }}>{count}</span>
          )}
        </div>
        {onSeeAll && items.length > 0 && (
          <button onClick={onSeeAll} className="ceo-btn ceo-btn-ghost" style={{ padding: '4px 10px', fontSize: 11 }}>
            See all <ChevronRight className="h-3 w-3" />
          </button>
        )}
      </div>
      {items.length > 0 ? (
        <div className="space-y-1">{items}</div>
      ) : (
        <p className="text-xs italic" style={{ color: 'var(--text-low)' }}>{empty || 'Nothing to show.'}</p>
      )}
    </div>
  );
}

function BriefRow({ title, due, priority, overdue }) {
  return (
    <div className="ceo-todo-row">
      <div className={`ceo-priority-dot ${priority || 'normal'}`} style={{ marginTop: 6 }} />
      <div className="flex-1 min-w-0">
        <div className="ceo-todo-title">{title}</div>
        {due && (
          <div className="text-[11px] mt-0.5" style={{ color: overdue ? 'var(--critical)' : 'var(--text-low)' }}>
            {overdue ? 'Overdue · ' : ''}{formatDue(due)}
          </div>
        )}
      </div>
    </div>
  );
}

function formatDue(d) {
  try {
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return d; }
}
